// English translation and "differs from our standard wording" for one contract file.
//
// Both come from the same AI read as the file summary (LegalContractFileAnalysis
// extracted_data), so there is nothing extra to fetch. Shared by the classic and
// new-look contract pages. The AI reader is off on the test systems: every state
// where it produced nothing says so in words instead of showing an empty box.

import React from 'react';

import './ContractReview.css';

const BASIS_NOTE = {
  commercial_terms_only:
    'Compared with the commercial terms of our contract only: the standard terms and conditions have not been supplied yet.',
};

export const ContractReview = ({ analysis }) => {
  if (!analysis) return null;
  if (analysis.status === 'failed') {
    return (
      <div className="contract-review contract-review--off" role="status">
        The English translation and the differences from our standard wording could not
        be produced: the AI reader did not finish this file.
      </div>
    );
  }
  if (analysis.status !== 'completed') return null;

  const data = analysis.extracted_data || {};
  const hasReview = 'differences' in data || 'needs_translation' in data || 'language' in data;
  if (!hasReview) {
    return (
      <div className="contract-review contract-review--off" role="status">
        This file was read before translation and comparison existed. Press
        &ldquo;Retry analysis&rdquo; to add them.
      </div>
    );
  }

  const pairs = Array.isArray(data.translation_pairs) ? data.translation_pairs : [];
  const differences = Array.isArray(data.differences) ? data.differences : [];

  return (
    <div className="contract-review">
      {data.language_unknown && (
        <p className="contract-review__off">The language of this contract could not be read, so no translation was made.</p>
      )}
      {data.needs_translation && (
        <section className="contract-review__section">
          <h4 className="contract-review__head">English translation ({String(data.language)} original)</h4>
          {data.translation_error && (
            <p className="contract-review__off">{data.translation_error} The original is unchanged.</p>
          )}
          {pairs.length > 0 && (
            <div className="contract-review__pairs">
              <div className="contract-review__col-head">Original</div>
              <div className="contract-review__col-head">English</div>
              {pairs.map((pair, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={idx}>
                  <div className="contract-review__cell" lang="und">{pair.original}</div>
                  <div className="contract-review__cell">{pair.english || 'This block could not be translated.'}</div>
                </React.Fragment>
              ))}
            </div>
          )}
          {data.translation_truncated && (
            <p className="contract-review__off">
              The contract is long: only the first part is translated.
            </p>
          )}
        </section>
      )}
      <section className="contract-review__section">
        <h4 className="contract-review__head">Differs from our standard wording</h4>
        {BASIS_NOTE[data.standard_basis] && (
          <p className="contract-review__off">{BASIS_NOTE[data.standard_basis]}</p>
        )}
        {differences.length === 0 ? (
          <p className="contract-review__none">No clause was found that differs from our standard wording.</p>
        ) : (
          <ul className="contract-review__diffs">
            {differences.map((item, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={idx} className="contract-review__diff">
                <mark className="contract-review__mark">{item.clause}</mark>
                <span className="contract-review__why">{item.difference}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default ContractReview;
